import Image from "next/image";
import Link from "next/link";
import React from "react";
import { FaPhone } from "react-icons/fa";

export interface HeroCta {
  label?: string;
  link?: string;
}

export interface NewHeroSectionProps {
  heading?: string;
  subheading?: string;
  supportingDescription?: string;
  primaryCta?: HeroCta;
  secondaryCta?: HeroCta;
  heroImageUrl?: string;
  heroImageAltText?: string;
}

/**
 * New Hero Section - Doctor-Led Approach
 * Based on final homepage design requirements
 */
export default function NewHeroSection({
  heading = "Doctor-Led Hair Restoration & Hair Loss Care",
  subheading = "Treatment suitability and outcomes depend on individual medical evaluation.",
  supportingDescription = "Every recommendation at Trichos begins with a doctor-led consultation to determine what is medically appropriate for you.",
  primaryCta = {
    label: "Start With a Medical Consultation",
    link: "/appointment",
  },
  secondaryCta = {
    label: "Explore Treatment Options",
    link: "/blog",
  },
  heroImageUrl,
  heroImageAltText = "Dr. John Watts in consultation at Trichos clinic",
}: NewHeroSectionProps) {
  return (
    <section className="py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-8 md:grid-cols-2 md:items-center md:gap-12">
          {/* Left: Text Content */}
          <div className="text-left">
            <h1 className="mb-4 text-4xl font-bold leading-tight text-gray-900 md:text-5xl lg:text-6xl">
              {heading}
            </h1>

            <p className="mb-4 text-lg italic text-gray-700 md:text-xl">
              {subheading}
            </p>

            <p className="mb-8 text-base text-gray-600">
              {supportingDescription}
            </p>

            {/* Primary CTA */}
            <div className="mb-4">
              {primaryCta?.label && primaryCta?.link && (
                <Link
                  href={primaryCta.link}
                  className="inline-block rounded-full bg-black px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-800 md:text-base"
                  style={{ color: "#F7E9AE" }}
                >
                  {primaryCta.label}
                </Link>
              )}
            </div>

            {/* Secondary CTA */}
            <div>
              {secondaryCta?.label && secondaryCta?.link && (
                <Link
                  href={secondaryCta.link}
                  className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-800 md:text-base"
                  style={{ color: "#F7E9AE" }}
                >
                  <FaPhone className="size-4" />
                  {secondaryCta.label}
                </Link>
              )}
            </div>
          </div>

          {/* Right: Visual - Doctor in Clinic */}
          <div className="relative">
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-gray-100 shadow-lg">
              {heroImageUrl ? (
                <Image
                  src={heroImageUrl}
                  alt={heroImageAltText}
                  fill
                  className="object-cover"
                  priority
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-gradient-to-br from-violet-50 to-violet-100">
                  <div className="text-center">
                    <div className="mb-4 text-6xl">👨‍⚕️</div>
                    <p className="text-sm text-gray-600">
                      Dr. John Watts in consultation
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      (Replace with actual clinic image)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
